import React, { useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { chain, debounce, extend, has, findIndex, isEmpty, isFunction } from 'lodash';
import { ConnectedLocationsList } from './LocationsList';
import {
    Config,
    CurrentAccommodationSearchSessionStorage,
    LocationUtils,
    TrackingUtils,
    withPageContext
} from '../../utils';
import { LocationNotFound } from './LocationNotFound';
import {
    GeoNodeType,
    LocationUserSubscription,
    LocationType,
    LegTypeProps,
    EntryPoint,
    TabTypeProp,
    TabTypePropDefault,
    SourceOptionsType,
    LegType,
    LocationHotelsSuppliers,
} from '../../models';
import {
    getLocationAutocomplete,
    getHotelLocationAutocomplete,
} from '../../redux/locationAutocomplete/actions';
import {
    DP_SEARCH_FORM_DEPARTURE_NO_MATCHES,
    DP_SEARCH_FORM_DESTINATION_NO_MATCHES,
    HSA_SEARCH_FORM_ROOMS_NO_MATCHES,
    withTrackComponentDisplayed
} from '../../utils/HtmlEvents';
import { LocationsListSkeleton } from './LocationsListSkeleton';

const supplier = LocationHotelsSuppliers.HSA;
const getLocationNotFoundWithTrackComponentDisplayed = (activeProduct, departureOrArrival) => {
    let LocationNotFoundWithTrackComponentDisplayed = LocationNotFound;
    if (activeProduct === EntryPoint.HOTELS) {
        LocationNotFoundWithTrackComponentDisplayed = withTrackComponentDisplayed(LocationNotFound, HSA_SEARCH_FORM_ROOMS_NO_MATCHES);
    } else if (activeProduct === EntryPoint.DP) {
        const htmlEventID = departureOrArrival === LegType.ARRIVAL ?
            DP_SEARCH_FORM_DESTINATION_NO_MATCHES : DP_SEARCH_FORM_DEPARTURE_NO_MATCHES;
        LocationNotFoundWithTrackComponentDisplayed = withTrackComponentDisplayed(LocationNotFound, htmlEventID);
    }
    return LocationNotFoundWithTrackComponentDisplayed;
};

const showLocationsListSkeleton = (activeProduct, completedFetch, prevSuggestionsListRef) => {
    return !completedFetch && activeProduct === EntryPoint.HOTELS && isEmpty(prevSuggestionsListRef.current);
};

const showPrevSuggestionsList = (completedFetch, prevSuggestionsListRef) => {
    return !completedFetch && !isEmpty(prevSuggestionsListRef.current);
};

const LocationAutocomplete = ({
    displayedWord,
    onClickItem,
    departureOrArrival,
    fetchedLocations,
    isFixedWidth,
    activeProduct,
    userAuthentication,
    getLocationAutocomplete,
    getHotelLocationAutocomplete,
    trackEvent,
    propsAutocompleteOptions,
}) => {
    const autocompleteOptions = propsAutocompleteOptions || Config.get('autocompleteOptions');

    const getUserSubscription = (userAuthentication) => {
        const {
            usersResponse: { membership: isMembership },
        } = userAuthentication;
        return isMembership ? LocationUserSubscription.PRIME : LocationUserSubscription.NON_PRIME;
    };

    const getDisplayableSuggestedLocations = (fetchedLocations) => {
        const locationProductType = LocationUtils.getLocationProductType(
            departureOrArrival,
            activeProduct,
        );
        const filterParams = {
            fetchedLocations,
            displayedWord,
            maxAutocompleteSuggestions: autocompleteOptions.autocompleteMaxSuggestions,
            locationProductType,
        };
        const filteredLocations = activeProduct === EntryPoint.HOTELS
            ? LocationUtils.filterSuggestedLocationsSearchByRegion({
                ...filterParams,
            })
            : LocationUtils.filterSuggestedLocations({
                ...filterParams,
            });

        return filteredLocations.map((location) => {
            let values = [location.name];
            let match;

            if (location.type !== LocationType.REGION && Array.isArray(location.locationNames)) {
                values = values.concat(location.locationNames);
            }

            match = values.find((name) => name.toLowerCase().indexOf(displayedWord) >= 0);

            fillDisplayName(location);

            return chain(location)
                .extend({
                    displayName:
                        (location.geoNodeType === GeoNodeType.CITY &&
                        location.type === LocationType.IATA_CODE
                            ? location.city
                            : location.name) || match,
                })
                .tap(formatLocation)
                .value();
        });
    };

    const getSuggestionList = (locations) => {
        return getDisplayableSuggestedLocations(locations);
    };

    const getLocationPositionFromSuggestionList = (suggestionList, searchValue) => {
        return findIndex(suggestionList, ({ name, value }) => {
            return name === searchValue || value === searchValue;
        });
    };

    const formatLocation = (loc) => {
        const formatter = {
            AIRPORT: formatCityOrAirportTypeText,
            CITY: formatCityOrAirportTypeText,
            IATA_CODE: formatIataTypeText,
        };
        let type;
        if (loc.relatedLocations?.length > 1) {
            loc.relatedLocations.forEach((rel) => {
                type = rel.geoNodeType || rel.type;
                extend(rel, {
                    text: has(formatter, type.toUpperCase())
                        ? formatter[type.toUpperCase()](rel)
                        : '',
                });
            });
        }
        if (!has(loc, 'displayName') || isEmpty(loc.displayName)) {
            extend(loc, {
                displayName: loc.city,
            });
        }
        type = loc.geoNodeType || loc.type || LocationType.CITY;

        return extend(loc, {
            text: isFunction(formatter[type.toUpperCase()])
                ? formatter[type.toUpperCase()](loc)
                : '',
        });
    };

    const formatIataTypeText = (loc) => {
        const { relatedLocations, displayName, name } = loc;
        return !isEmpty(relatedLocations)
            ? `${displayName} - ${relatedLocations[0].name}`
            : `${displayName} - ${name}`;
    };

    const formatCityOrAirportTypeText = (loc) => {
        return loc.displayName || loc.name;
    };

    const fillDisplayName = (location) => {
        if (!isEmpty(location.relatedLocations)) {
            location.relatedLocations.forEach((rel) => {
                extend(rel, {
                    displayName: rel.name,
                });
            });
        }
    };

    const locationProductType = LocationUtils.getLocationProductType(
        departureOrArrival,
        activeProduct,
    );
    const subscription = getUserSubscription(userAuthentication);
    const fetchedLocation = LocationUtils.getFetchedLocations(
        fetchedLocations,
        locationProductType,
        departureOrArrival,
        activeProduct,
        displayedWord,
        subscription,
    );
    const completedFetch = fetchedLocation && !fetchedLocation.isFetching;
    const loadTime = fetchedLocation?.executionTime;
    const locations = completedFetch && fetchedLocation.locationAutocomplete;
    const suggestionsList = getSuggestionList(locations);
    const prevSuggestionsListRef = useRef([]);
    const isKeywordLongEnough = LocationUtils.isKeywordLongEnoughAccordingWithLanguage(
        displayedWord,
        autocompleteOptions,
        activeProduct,
    );

    const handleTracking = (label) => {
        const action = TrackingUtils.ACTION[activeProduct.toUpperCase()];
        (activeProduct === EntryPoint.HOTELS || activeProduct === EntryPoint.DP) && trackEvent({ action, label });
    };

    useEffect(() => {
        if (!isKeywordLongEnough) {
            prevSuggestionsListRef.current = [];
        }
        if (isKeywordLongEnough && completedFetch) {
            if (loadTime) {
                handleTracking(TrackingUtils.LABEL.hotelsDestinationTyped('', supplier));
                handleTracking(TrackingUtils.LABEL.hotelsDestinationLoadTime(loadTime, supplier));
            }
            if (isEmpty(suggestionsList)) {
                handleTracking(
                    TrackingUtils.LABEL.inputErrorNoMatches({
                        activeProduct,
                        inputField: departureOrArrival === LegType.ARRIVAL ? 'dest' : 'dep',
                        searchValue: displayedWord,
                        supplier,
                    }),
                );
            }
            prevSuggestionsListRef.current = suggestionsList;
        }
    }, [isKeywordLongEnough, completedFetch, suggestionsList, departureOrArrival]);

    const _onClickItem = (currentSearch) => {
        const { name, value, displayName } = currentSearch;
        handleTracking(TrackingUtils.LABEL.hotelsDestinationTyped(displayedWord, supplier));
        const position = getLocationPositionFromSuggestionList(
            suggestionsList,
            name || value || displayName,
        );
        if (activeProduct === EntryPoint.HOTELS) {
            CurrentAccommodationSearchSessionStorage.setSegment({ destination: currentSearch });
        }
        handleTracking(
            TrackingUtils.LABEL.hotelsDestinationSelectPos(
                position + 1,
                currentSearch,
                supplier,
            ),
        );
        onClickItem(currentSearch);
    };

    const triggerAutocomplete = useCallback(
        debounce((displayedWord) => {
            const subscription = userAuthentication.usersResponse.membership
                ? LocationUserSubscription.PRIME
                : LocationUserSubscription.NON_PRIME;
            if (activeProduct === EntryPoint.HOTELS) {
                getHotelLocationAutocomplete({
                    activeProduct,
                    displayedWord,
                    autocompleteOptions,
                    subscription,
                });
            } else {
                const locationProductType = LocationUtils.getLocationProductType(
                    departureOrArrival,
                    activeProduct,
                );
                getLocationAutocomplete({
                    activeProduct,
                    displayedWord,
                    autocompleteOptions,
                    departureOrArrival,
                    locationProductType,
                });
            }
        }, 300),
        [activeProduct, departureOrArrival, getLocationAutocomplete, getHotelLocationAutocomplete, userAuthentication.usersResponse.membership]
    );

    useEffect(() => {
        if (
            LocationUtils.isKeywordLongEnoughAccordingWithLanguage(
                displayedWord,
                autocompleteOptions,
                activeProduct,
            )
        ) {
            triggerAutocomplete(displayedWord);
        }
    }, [displayedWord]);

    let content;
    if (!isKeywordLongEnough) {
        content = null;
    } else if (showLocationsListSkeleton(activeProduct, completedFetch, prevSuggestionsListRef)) {
        return <LocationsListSkeleton />;
    } else if (showPrevSuggestionsList(completedFetch, prevSuggestionsListRef)) {
        content = (
            <ConnectedLocationsList
                displayedWord={displayedWord}
                departureOrArrival={departureOrArrival}
                suggestionsList={prevSuggestionsListRef.current}
                onClickItem={_onClickItem}
                isFixedWidth={isFixedWidth}
            />
        );
    } else if (completedFetch && !isEmpty(suggestionsList)) {
        content = (
            <ConnectedLocationsList
                displayedWord={displayedWord}
                departureOrArrival={departureOrArrival}
                suggestionsList={suggestionsList}
                onClickItem={_onClickItem}
                isFixedWidth={isFixedWidth}
            />
        );
    } else if (completedFetch) {
        let LocationNotFoundWithTrackComponentDisplayed = getLocationNotFoundWithTrackComponentDisplayed(activeProduct, departureOrArrival);
        content = (
            <LocationNotFoundWithTrackComponentDisplayed
                activeProduct={activeProduct}
                displayedWord={displayedWord}
            />
        );
    }
    return (<>{content}</>);
};

LocationAutocomplete.propTypes = {
    ...SourceOptionsType,
    departureOrArrival: LegTypeProps.isRequired,
    fetchedLocations: PropTypes.object.isRequired,
    getLocationAutocomplete: PropTypes.func.isRequired,
    getHotelLocationAutocomplete: PropTypes.func.isRequired,
    activeProduct: TabTypeProp,

    autocompleteOptions: PropTypes.shape({
        accommodationAutocompleteMinChars: PropTypes.number.isRequired,
        autocompleteMinChars: PropTypes.number.isRequired,
        defaultMinChars: PropTypes.number.isRequired,
        autocompleteMaxSuggestions: PropTypes.number.isRequired,
        coherentCharsRegex: PropTypes.string.isRequired,
    }),
};

LocationAutocomplete.defaultProps = {
    activeProduct: TabTypePropDefault,
    autocompleteOptions: null,
};

const mapStateToProps = ({ locationAutocomplete, activeProduct, userAuthentication }) => ({
    fetchedLocations: locationAutocomplete,
    activeProduct: activeProduct.activeProduct,
    userAuthentication: userAuthentication,
});

const connected = connect(mapStateToProps, {
    getLocationAutocomplete,
    getHotelLocationAutocomplete,
})(withPageContext(LocationAutocomplete));

export { connected as ConnectedLocationAutocomplete };
